import { PROFILE_SIDEBAR_NODE } from "@/constants/profileConstants";
import { ProfileChannel } from "@/types/profile";
import { getParentChannels } from "@/utilities/jobs/profileUtils";
import { isValidEmail } from "@/utilities/supportUtilities";
import { channelNodeId, parseSidebarNodeId, SubmitJobProfileFormState } from "@/utilities/profiles/profileFormUtils";
import {
  getReleaseAllowedImageSelection,
  getSelectedEnvironments,
  getSelectedPublishJobTypeKeys,
  getStageImageSelection,
  requiresReleaseDetails
} from "@/utilities/profiles/channelFormUtils";

export const profileFieldKey = (nodeId: string, field: string): string => `${nodeId}.${field}`;

export interface ProfileValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export const validateGeneralInfo = (
  state: Pick<SubmitJobProfileFormState, "plc" | "profileName" | "owners" | "releaseTypes" | "platforms">
): ProfileValidationResult => {
  const errors: Record<string, string> = {};
  const nodeId = PROFILE_SIDEBAR_NODE.GENERAL;

  if (!state.plc.trim()) {
    errors[profileFieldKey(nodeId, "plc")] = "Item is required";
  }
  if (!state.profileName.trim()) {
    errors[profileFieldKey(nodeId, "profileName")] = "Job Profile Name is required";
  }
  if (state.releaseTypes.length === 0) {
    errors[profileFieldKey(nodeId, "releaseTypes")] = "Select at least one Publishing Type";
  }
  if (state.platforms.length === 0) {
    errors[profileFieldKey(nodeId, "platforms")] = "Select at least one Platform";
  }

  state.owners.forEach((owner, index) => {
    if (!owner.name.trim()) {
      errors[profileFieldKey(nodeId, `owner-${index}-name`)] = "Name is required";
    }
    if (!owner.email.trim()) {
      errors[profileFieldKey(nodeId, `owner-${index}-email`)] = "Email is required";
    } else if (!isValidEmail(owner.email.trim())) {
      errors[profileFieldKey(nodeId, `owner-${index}-email`)] = "Enter a valid email address";
    }
  });

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const validateChannel = (channel: ProfileChannel, nodeId: string): ProfileValidationResult => {
  const errors: Record<string, string> = {};

  const maxBuilds = channel.Release?.MaxBuilds;
  if (maxBuilds === undefined || Number.isNaN(maxBuilds)) {
    errors[profileFieldKey(nodeId, "maxBuilds")] = "Max Builds is required";
  } else if (maxBuilds < 0) {
    errors[profileFieldKey(nodeId, "maxBuilds")] = "Max Builds cannot be negative";
  }

  if (getSelectedEnvironments(channel).length === 0) {
    errors[profileFieldKey(nodeId, "environment")] = "Select at least one Environment";
  }

  if (getSelectedPublishJobTypeKeys(channel).length === 0) {
    errors[profileFieldKey(nodeId, "publishImages")] = "Select at least one Image to Publish";
  }

  if (requiresReleaseDetails(channel)) {
    if (getReleaseAllowedImageSelection(channel).length === 0) {
      errors[profileFieldKey(nodeId, "releaseDetails")] = "Select at least one Allowed Image in Release Details";
    }
    const previewRequired = getStageImageSelection(channel, "Preview", "RequiredImages");
    const liveRequired = getStageImageSelection(channel, "Live", "RequiredImages");
    if (previewRequired.length === 0 && liveRequired.length === 0) {
      errors[profileFieldKey(nodeId, "requiredImages")] =
        "Select at least one Preview or Live Required Image";
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const validateNode = (
  nodeId: string,
  state: Pick<SubmitJobProfileFormState, "plc" | "profileName" | "owners" | "releaseTypes" | "platforms" | "channels">
): ProfileValidationResult => {
  const parsed = parseSidebarNodeId(nodeId);
  if (parsed.type === "general") {
    return validateGeneralInfo(state);
  }
  if (parsed.type === "review") {
    return { isValid: true, errors: {} };
  }
  if (parsed.type === "channel" && parsed.name) {
    const channel = state.channels.find((entry) => entry.Name === parsed.name);
    if (!channel) {
      return { isValid: false, errors: {} };
    }
    return validateChannel(channel, nodeId);
  }
  return { isValid: true, errors: {} };
};

export const getAllChannelNodeIds = (channels: ProfileChannel[]): string[] =>
  getParentChannels(channels).map((parent) => channelNodeId(parent.Name));

export const isChannelInfoComplete = (
  state: Pick<SubmitJobProfileFormState, "plc" | "profileName" | "owners" | "releaseTypes" | "platforms" | "channels">
): boolean => getAllChannelNodeIds(state.channels).every((nodeId) => validateNode(nodeId, state).isValid);

export const hasNodeErrors = (fieldErrors: Record<string, string>, nodeId: string): boolean =>
  Object.keys(fieldErrors).some((key) => key.startsWith(`${nodeId}.`));

export const stripNodeErrors = (fieldErrors: Record<string, string>, nodeId: string): Record<string, string> => {
  const next = { ...fieldErrors };
  Object.keys(next).forEach((key) => {
    if (key.startsWith(`${nodeId}.`)) {
      delete next[key];
    }
  });
  return next;
};
